import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Tooltip,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import addFill from "@iconify/icons-eva/plus-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import filterFill from "@iconify/icons-eva/funnel-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import searchFill from "@iconify/icons-eva/search-fill";
import { DateTimePicker } from "../../FormikDateTimePicker";
import { dashboardDateFormatter } from "../../../utils/formatTime";

// Filter criteria structure
const createEmptyFilter = () => ({
  id: Date.now() + Math.random(),
  field: "",
  operator: "",
  value: "",
  valueType: "string",
});

// Available operators by field type
const getOperatorsForField = (field) => {
  const numberFields = [
    "kw_consumed",
    "totalAmountPaid",
    "totalAmountCharged",
    "totalRefund",
    "price",
  ];
  const dateFields = ["starttime", "stoptime"];
  const selectFields = ["priceUnit"];
  const stringFields = [
    "transactionId",
    "paymentId",
    "email",
    "phone",
    "chargepoint_id",
    "connectorId",
    "payment_type",
    "notes",
  ];

  if (selectFields.includes(field)) {
    return [
      { value: "equals", label: "Equals" },
      { value: "notEquals", label: "Not Equals" },
      { value: "isEmpty", label: "Is Empty" },
      { value: "isNotEmpty", label: "Is Not Empty" },
    ];
  }

  if (numberFields.includes(field)) {
    return [
      { value: "equals", label: "Equals" },
      { value: "notEquals", label: "Not Equals" },
      { value: "greaterThan", label: "Greater Than" },
      { value: "greaterThanOrEqual", label: "Greater Than or Equal" },
      { value: "lessThan", label: "Less Than" },
      { value: "lessThanOrEqual", label: "Less Than or Equal" },
      { value: "between", label: "Between" },
      { value: "isEmpty", label: "Is Empty" },
      { value: "isNotEmpty", label: "Is Not Empty" },
    ];
  }

  if (dateFields.includes(field)) {
    return [
      { value: "equals", label: "Equals" },
      { value: "notEquals", label: "Not Equals" },
      { value: "after", label: "After" },
      { value: "onOrAfter", label: "On or After" },
      { value: "before", label: "Before" },
      { value: "onOrBefore", label: "On or Before" },
      { value: "between", label: "Between" },
      { value: "isEmpty", label: "Is Empty" },
      { value: "isNotEmpty", label: "Is Not Empty" },
    ];
  }

  // String fields
  return [
    { value: "contains", label: "Contains" },
    { value: "notContains", label: "Does Not Contain" },
    { value: "equals", label: "Equals" },
    { value: "notEquals", label: "Not Equals" },
    { value: "startsWith", label: "Starts With" },
    { value: "endsWith", label: "Ends With" },
    { value: "isEmpty", label: "Is Empty" },
    { value: "isNotEmpty", label: "Is Not Empty" },
    { value: "isAnyOf", label: "Is Any Of" },
  ];
};

// Field options
const fieldOptions = [
  { value: "transactionId", label: "Transaction ID", type: "string" },
  { value: "paymentId", label: "Payment ID", type: "string" },
  { value: "email", label: "Email", type: "string" },
  { value: "phone", label: "Phone", type: "string" },
  { value: "id_tag", label: "Tag (RFID)", type: "string" },
  { value: "parenttag", label: "Parent Tag", type: "string" },
  { value: "chargepoint_id", label: "ChargeBox ID", type: "string" },
  { value: "connectorId", label: "Connector", type: "string" },
  { value: "payment_type", label: "Payment Type", type: "string" },
  { value: "kw_consumed", label: "kWh Consumed", type: "number" },
  { value: "price", label: "Price", type: "number" },
  { value: "priceUnit", label: "Price Unit", type: "select", options: [{ value: "per_kw", label: "/kWh" }, { value: "per_min", label: "/min" }], },
  { value: "totalAmountPaid", label: "Amount Paid", type: "number" },
  { value: "totalAmountCharged", label: "Charging Cost", type: "number" },
  { value: "totalRefund", label: "Refund / Wallet Balance", type: "number" },
  { value: "starttime", label: "Start Time", type: "date" },
  { value: "stoptime", label: "Stop Time", type: "date" },
  { value: "notes", label: "Notes", type: "string" },
];

// Server-side date-range window options for the Transactions tab. Default is
// last 7 days; a custom range is capped at 90 days (also enforced by the backend).
const DATE_RANGE_OPTIONS = [
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "last60days", label: "Last 60 Days" },
  { value: "last90days", label: "Last 90 Days" },
  { value: "custom", label: "Custom Range" },
];
const MAX_CUSTOM_RANGE_DAYS = 90;

export const CompactFilter = ({
  filters = [],
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  searchText = "",
  onSearchChange,
  onExpandFilter,
  dateRange = "last7days",
  dateFrom = "",
  dateTo = "",
  onDateRangeChange,
}) => {
  const [localSearchText, setLocalSearchText] = useState(searchText);
  const [localFilters, setLocalFilters] = useState(
    filters.length > 0 ? filters : [createEmptyFilter()]
  );
  const [logicalOperator, setLogicalOperator] = useState("AND");
  const [isExpanded, setIsExpanded] = useState(false);

  // Custom date-range pickers (only shown when dateRange === "custom").
  const [localFrom, setLocalFrom] = useState(dateFrom);
  const [localTo, setLocalTo] = useState(dateTo);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    setLocalSearchText(searchText);
  }, [searchText]);

  useEffect(() => {
    setLocalFrom(dateFrom);
  }, [dateFrom]);

  useEffect(() => {
    setLocalTo(dateTo);
  }, [dateTo]);

  const handleRangeSelect = (value) => {
    setDateError("");
    if (value === "custom") {
      // Keep the prior data until both custom dates are chosen; the parent falls
      // back to the default window while the custom range is incomplete.
      onDateRangeChange && onDateRangeChange("custom", localFrom, localTo);
    } else {
      onDateRangeChange && onDateRangeChange(value);
    }
  };

  const handleCustomDateChange = (nextFrom, nextTo) => {
    setLocalFrom(nextFrom);
    setLocalTo(nextTo);

    if (!nextFrom || !nextTo) {
      setDateError("");
      return; // wait until both ends are picked
    }

    const start = new Date(nextFrom);
    const end = new Date(nextTo);
    if (end < start) {
      setDateError("'To' date cannot be before 'From' date");
      return;
    }
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (days > MAX_CUSTOM_RANGE_DAYS) {
      setDateError(`Range cannot exceed ${MAX_CUSTOM_RANGE_DAYS} days`);
      return;
    }
    setDateError("");
    onDateRangeChange && onDateRangeChange("custom", nextFrom, nextTo);
  };

  const handleAddFilter = () => {
    const newFilter = createEmptyFilter();
    setLocalFilters((prev) => [...prev, newFilter]);
    // Auto-collapse transaction overview when adding a filter
    if (onExpandFilter) {
      onExpandFilter();
    }
  };

  const handleRemoveFilter = (filterId) => {
    const newFilters = localFilters.filter((filter) => filter.id !== filterId);
    setLocalFilters(newFilters);

    // Notify parent component about the filter change
    const validFilters = newFilters.filter(
      (filter) =>
        filter.field &&
        filter.operator &&
        (filter.value || ["isEmpty", "isNotEmpty"].includes(filter.operator))
    );
    onFiltersChange(validFilters, logicalOperator);
  };

  const handleFilterChange = (filterId, field, value) => {
    setLocalFilters((prev) =>
      prev.map((filter) => {
        if (filter.id === filterId) {
          const updatedFilter = { ...filter, [field]: value };

          // Reset operator and value when field changes
          if (field === "field") {
            updatedFilter.operator = "";
            updatedFilter.value = "";
            updatedFilter.valueType =
              fieldOptions.find((f) => f.value === value)?.type || "string";
          }

          // Reset value when operator changes
          if (field === "operator") {
            updatedFilter.value = "";
          }

          return updatedFilter;
        }
        return filter;
      })
    );
  };

  const handleApplyFilters = () => {
    const validFilters = localFilters.filter(
      (filter) =>
        filter.field &&
        filter.operator &&
        (filter.value || ["isEmpty", "isNotEmpty"].includes(filter.operator))
    );

    onFiltersChange(validFilters, logicalOperator);
    onApplyFilters();
  };

  const handleClearFilters = () => {
    setLocalFilters([createEmptyFilter()]);
    setLogicalOperator("AND");
    onClearFilters();
    // Clear search text as well
    setLocalSearchText("");
    onSearchChange("");
    // Notify parent component about the filter change
    onFiltersChange([], "AND");
  };

  const renderValueInput = (filter) => {
    const { operator, value, valueType } = filter;

    if (["isEmpty", "isNotEmpty"].includes(operator)) {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: "info.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.200",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            color="info.main"
            sx={{ fontWeight: 500 }}
          >
            {operator === "isEmpty"
              ? "🔍 Will find empty values"
              : "🔍 Will find non-empty values"}
          </Typography>
        </Box>
      );
    }

    if (operator === "between") {
      // Date type: use custom DateTimePicker for both from/to
      if (valueType === "date") {
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ width: 220 }}>
              <DateTimePicker
                size="small"
                label="From"
                mode="date"
                value={Array.isArray(value) && value[0] ? new Date(value[0]) : null}
                onChange={(newVal) => {
                  const formatted = newVal ? dashboardDateFormatter(newVal) : "";
                  const newValue = Array.isArray(value)
                    ? [formatted, value[1]]
                    : [formatted, ""];
                  handleFilterChange(filter.id, "value", newValue);
                }}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              to
            </Typography>
            <Box sx={{ width: 220 }}>
              <DateTimePicker
                size="small"
                label="To"
                mode="date"
                value={Array.isArray(value) && value[1] ? new Date(value[1]) : null}
                onChange={(newVal) => {
                  const formatted = newVal ? dashboardDateFormatter(newVal) : "";
                  const newValue = Array.isArray(value)
                    ? [value[0], formatted]
                    : ["", formatted];
                  handleFilterChange(filter.id, "value", newValue);
                }}
              />
            </Box>
          </Box>
        );
      }

      // Number / text between
      return (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder={
              valueType === "number"
                ? "Min value"
                : "From"
            }
            value={Array.isArray(value) ? value[0] || "" : ""}
            onChange={(e) => {
              const newValue = Array.isArray(value)
                ? [e.target.value, value[1]]
                : [e.target.value, ""];
              handleFilterChange(filter.id, "value", newValue);
            }}
            type={
              valueType === "number"
                ? "number"
                : valueType === "date"
                ? "date"
                : "text"
            }
            sx={{
              width: 120,
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
              },
            }}
            InputProps={{
              startAdornment: (
                <Typography
                  variant="caption"
                  sx={{ mr: 1, color: "text.secondary" }}
                >
                  From:
                </Typography>
              ),
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            to
          </Typography>
          <TextField
            size="small"
            placeholder={
              valueType === "date"
                ? "End date"
                : valueType === "number"
                ? "Max value"
                : "To"
            }
            value={Array.isArray(value) ? value[1] || "" : ""}
            onChange={(e) => {
              const newValue = Array.isArray(value)
                ? [value[0], e.target.value]
                : ["", e.target.value];
              handleFilterChange(filter.id, "value", newValue);
            }}
            type={
              valueType === "number"
                ? "number"
                : valueType === "date"
                ? "date"
                : "text"
            }
            sx={{
              width: 120,
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
              },
            }}
            InputProps={{
              startAdornment: (
                <Typography
                  variant="caption"
                  sx={{ mr: 1, color: "text.secondary" }}
                >
                  To:
                </Typography>
              ),
            }}
          />
        </Box>
      );
    }

    if (operator === "isAnyOf") {
      return (
        <TextField
          size="small"
          placeholder="e.g., UPI, Card, Wallet (separate with commas)"
          value={Array.isArray(value) ? value.join(", ") : value || ""}
          onChange={(e) => {
            const values = e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v);
            handleFilterChange(filter.id, "value", values);
          }}
          sx={{
            minWidth: 250,
            "& .MuiOutlinedInput-root": {
              bgcolor: "white",
            },
          }}
          InputProps={{
            startAdornment: (
              <Typography
                variant="caption"
                sx={{ mr: 1, color: "text.secondary" }}
              >
                Values:
              </Typography>
            ),
          }}
        />
      );
    }

    // Handle select-type fields (e.g., Price Unit)
    if (valueType === "select") {
      const fieldConfig = fieldOptions.find((f) => f.value === filter.field);
      const selectOptions = fieldConfig?.options || [];

      if (operator === "isAnyOf") {
        return (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Values</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : value ? [value] : []}
              onChange={(e) =>
                handleFilterChange(filter.id, "value", e.target.value)
              }
              label="Values"
              renderValue={(selected) =>
                selected
                  .map(
                    (v) =>
                      selectOptions.find((o) => o.value === v)?.label || v
                  )
                  .join(", ")
              }
              sx={{ bgcolor: "white" }}
            >
              {selectOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      return (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={value || ""}
            onChange={(e) =>
              handleFilterChange(filter.id, "value", e.target.value)
            }
            label="Value"
            sx={{ bgcolor: "white" }}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Date type: use custom DateTimePicker
    if (valueType === "date") {
      return (
        <Box sx={{ minWidth: 220 }}>
          <DateTimePicker
            size="small"
            label="Date"
            mode="date"
            value={value ? new Date(value) : null}
            onChange={(newVal) => {
              handleFilterChange(filter.id, "value", newVal ? dashboardDateFormatter(newVal) : "");
            }}
          />
        </Box>
      );
    }

    const getPlaceholder = () => {
      if (valueType === "number") return "Enter number (e.g., 100.50)";
      return "Enter text value";
    };

    return (
      <TextField
        size="small"
        placeholder={getPlaceholder()}
        value={value || ""}
        onChange={(e) => handleFilterChange(filter.id, "value", e.target.value)}
        type={
          valueType === "number"
            ? "number"
            : valueType === "date"
            ? "date"
            : "text"
        }
        sx={{
          minWidth: 200,
          "& .MuiOutlinedInput-root": {
            bgcolor: "white",
          },
        }}
        InputProps={{
          startAdornment: (
            <Typography
              variant="caption"
              sx={{ mr: 1, color: "text.secondary" }}
            >
              Value:
            </Typography>
          ),
        }}
      />
    );
  };

  // Format applied filters for display
  const formatAppliedFilter = (filter) => {
    const fieldLabel =
      fieldOptions.find((f) => f.value === filter.field)?.label || filter.field;
    const operatorLabel =
      getOperatorsForField(filter.field).find(
        (op) => op.value === filter.operator
      )?.label || filter.operator;

    let valueDisplay = "";
    if (["isEmpty", "isNotEmpty"].includes(filter.operator)) {
      valueDisplay = "";
    } else if (filter.operator === "between" && Array.isArray(filter.value)) {
      valueDisplay = ` ${filter.value[0] || ""} - ${filter.value[1] || ""}`;
    } else if (filter.operator === "isAnyOf" && Array.isArray(filter.value)) {
      valueDisplay = ` ${filter.value.join(", ")}`;
    } else {
      valueDisplay = ` "${filter.value}"`;
    }

    return `${fieldLabel} ${operatorLabel}${valueDisplay}`;
  };

  const appliedFilters = localFilters
    .filter(
      (filter) =>
        filter.field &&
        filter.operator &&
        (filter.value || ["isEmpty", "isNotEmpty"].includes(filter.operator))
    )
    .map(formatAppliedFilter);

  const activeFiltersCount = appliedFilters.length;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Search and Filter Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
          p: 1.5,
          bgcolor: "grey.50",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Tooltip title="Add advanced filters to narrow down your search results">
          <Button
            variant={isExpanded ? "contained" : "outlined"}
            startIcon={<Icon icon={addFill} />}
            onClick={() => {
              const newExpandedState = !isExpanded;
              setIsExpanded(newExpandedState);
              // Auto-collapse transaction overview when expanding filters
              if (newExpandedState && onExpandFilter) {
                onExpandFilter();
              }
            }}
            size="small"
            sx={{
              minWidth: 120,
              fontWeight: 600,
              ...(isExpanded && {
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
              }),
            }}
          >
            {isExpanded ? "Hide Filters" : "Add Filter"}
          </Button>
        </Tooltip>

        <Tooltip title="Restrict the list to a recent time window (max 90 days)">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => handleRangeSelect(e.target.value)}
              label="Date range"
              sx={{ bgcolor: "white" }}
            >
              {DATE_RANGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="🔍 Search transactions by ID, payment ID, email, phone, charger, or payment type..."
            value={localSearchText}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalSearchText(newValue);
              if (newValue === "") {
                onSearchChange("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSearchChange(localSearchText);
              }
            }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon={filterFill}
                  style={{ marginRight: 8, color: "#1976d2" }}
                />
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => onSearchChange(localSearchText)}
                    edge="end"
                    size="small"
                  >
                    <Icon icon={searchFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                },
              },
            }}
          />
        </Box>

        {activeFiltersCount > 0 && (
          <Tooltip title="Remove all applied filters">
            <Button
              variant="outlined"
              startIcon={<Icon icon={refreshFill} />}
              onClick={handleClearFilters}
              size="small"
              color="error"
              sx={{
                minWidth: 100,
                fontWeight: 600,
                borderColor: "error.main",
                "&:hover": {
                  borderColor: "error.dark",
                  bgcolor: "error.light",
                  color: "error.dark",
                },
              }}
            >
              Clear All
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Custom date-range pickers */}
      {dateRange === "custom" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5,
            p: 1.5,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "grey.200",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            📅 Custom range:
          </Typography>
          <Box sx={{ width: 200 }}>
            <DateTimePicker
              size="small"
              label="From"
              mode="date"
              value={localFrom ? new Date(localFrom) : null}
              onChange={(newVal) =>
                handleCustomDateChange(
                  newVal ? dashboardDateFormatter(newVal) : "",
                  localTo
                )
              }
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            to
          </Typography>
          <Box sx={{ width: 200 }}>
            <DateTimePicker
              size="small"
              label="To"
              mode="date"
              value={localTo ? new Date(localTo) : null}
              onChange={(newVal) =>
                handleCustomDateChange(
                  localFrom,
                  newVal ? dashboardDateFormatter(newVal) : ""
                )
              }
            />
          </Box>
          {dateError ? (
            <Typography
              variant="caption"
              color="error.main"
              sx={{ fontWeight: 600 }}
            >
              {dateError}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Up to {MAX_CUSTOM_RANGE_DAYS} days
            </Typography>
          )}
        </Box>
      )}

      {/* Applied Filters Display */}
      {appliedFilters.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: "primary.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Icon icon={filterFill} width={14} height={14} color="#1976d2" />
            <Typography
              variant="body2"
              color="primary.main"
              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
            >
              Active Filters ({appliedFilters.length}):
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {appliedFilters.map((filterText, index) => (
              <Chip
                key={index}
                label={filterText}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => handleRemoveFilter(localFilters[index].id)}
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  height: 20,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    fontSize: "0.8rem",
                    "&:hover": {
                      color: "primary.light",
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Collapsible Filter Form */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "primary.200",
            borderRadius: 1,
            p: 2,
            bgcolor: "white",
            boxShadow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              p: 1.5,
              bgcolor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              🔧 Logic:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Combine filters</InputLabel>
              <Select
                value={logicalOperator}
                onChange={(e) => setLogicalOperator(e.target.value)}
                label="Combine filters"
              >
                <MenuItem value="AND">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">All match (AND)</Typography>
                    <Chip
                      label="Strict"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem", height: 16 }}
                    />
                  </Box>
                </MenuItem>
                <MenuItem value="OR">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Any match (OR)</Typography>
                    <Chip
                      label="Flexible"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem", height: 16 }}
                    />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="AND: All filters must be true. OR: At least one filter must be true.">
              <Icon
                icon={filterFill}
                style={{ color: "#666", cursor: "help" }}
              />
            </Tooltip>
          </Box>

          {localFilters.map((filter, index) => (
            <Box
              key={filter.id}
              sx={{
                mb: 2,
                p: 1.5,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                bgcolor: "grey.25",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                >
                  Filter #{index + 1}:
                </Typography>
                {index < localFilters.length - 1 && (
                  <Chip
                    label={logicalOperator}
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: "0.6rem", height: 18 }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>📋 Field</InputLabel>
                  <Select
                    value={filter.field}
                    onChange={(e) =>
                      handleFilterChange(filter.id, "field", e.target.value)
                    }
                    label="📋 Field"
                  >
                    {fieldOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                          <Chip
                            label={option.type}
                            size="small"
                            color={
                              option.type === "number"
                                ? "warning"
                                : option.type === "date"
                                ? "info"
                                : "default"
                            }
                            variant="outlined"
                            sx={{ fontSize: "0.5rem", height: 14 }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>⚡ Operator</InputLabel>
                  <Select
                    value={filter.operator}
                    onChange={(e) =>
                      handleFilterChange(filter.id, "operator", e.target.value)
                    }
                    label="⚡ Operator"
                    disabled={!filter.field}
                  >
                    {getOperatorsForField(filter.field).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography variant="body2">{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1, minWidth: 180 }}>
                  {renderValueInput(filter)}
                </Box>

                <Tooltip title="Remove this filter">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFilter(filter.id)}
                    disabled={localFilters.length === 1}
                    sx={{
                      color: "error.main",
                      "&:hover": {
                        bgcolor: "error.light",
                        color: "error.dark",
                      },
                    }}
                  >
                    <Icon icon={closeFill} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "grey.300",
            }}
          >
            <Tooltip title="Add another filter condition">
              <Button
                startIcon={<Icon icon={addFill} />}
                onClick={handleAddFilter}
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 600,
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: "0.75rem",
                  py: 0.5,
                  "&:hover": {
                    borderColor: "primary.dark",
                    bgcolor: "primary.light",
                    color: "primary.dark",
                  },
                }}
              >
                Add Filter
              </Button>
            </Tooltip>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {activeFiltersCount > 0
                  ? `${activeFiltersCount} filter${
                      activeFiltersCount > 1 ? "s" : ""
                    } ready`
                  : "No filters applied"}
              </Typography>
              <Button
                onClick={handleApplyFilters}
                variant="contained"
                size="small"
                disabled={activeFiltersCount === 0}
                sx={{
                  fontWeight: 600,
                  minWidth: 100,
                  fontSize: "0.75rem",
                  py: 0.5,
                  bgcolor: activeFiltersCount > 0 ? "success.main" : "grey.400",
                  "&:hover": {
                    bgcolor:
                      activeFiltersCount > 0 ? "success.dark" : "grey.500",
                  },
                }}
              >
                ✅ Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
